import { isEmpty, values, without } from 'lodash';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { mapValueObjectsToOptions } from '~/components/Shared/EcSelect/EcSelect.utils';
import { Filters_DEPRECATED } from '~/components/Shared/Filters_DEPRECATED/Filters_DEPRECATED';
import { FiltersPreview } from '~/components/Shared/Filters_DEPRECATED/subcomponents/FiltersPreview';
import * as Filters from '~/components/Shared/Filters_DEPRECATED/types';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { Field } from '~/evifields/src/types/Field';
import { FieldId } from '~/evifields/src/types/FieldId';
import { Filter } from '~/evifields/src/types/Filter';
import { Filters as FiltersType } from '~/evifields/src/types/Filters';
import { EnumValue } from '~/evifields/src/types/Value';
import { api } from '~/redux/api/createApi';
import {
  PilotSearchQuery,
  testIsSupportedFilterType,
  toFilters,
} from '~/redux/api/methods/search';
import { getDefaultMoreFilterFieldIds } from '~/redux/api/transformers/search';
import { slices } from '~/redux/slices';
import { DropdownFieldValue } from '~/types/Field';
import { Nullable } from '~/types/Nullable';
import LoadingSpinner from '~/ui/components/LoadingSpinner/LoadingSpinner';
import { debounce } from '~/utils/debounce';

import { flattenFolderTree } from '../util';

type FilterSerializerProps = {
  field: Filters.Field;
  values?: DropdownFieldValue[];
};

type Fields = Record<FieldId, Field>;
type FieldValues = { [fieldId: string]: DropdownFieldValue[] };

type FilterProps = { fields: Fields; fieldValues: FieldValues };

const updateSettings = (field: Filters.Field): Field => {
  switch (field.type) {
    case 'enum':
    case 'enum_set':
      break;

    default:
      return field;
  }

  const shouldEnableSelectAll = (filter: Filter<EnumValue>) => {
    return (
      field.type === 'enum_set' &&
      (filter.operatorId === 'contains_any' ||
        filter.operatorId === 'not_contains_any')
    );
  };

  field.settings = {
    ...field.settings,
    __testEnableSelectAll: shouldEnableSelectAll,
  };

  return field;
};

const attachRenderer = (field: Filters.Field): Field => {
  // only need to attach data source for single/multi select filters
  // maybe one day we will support suggestions for text and/or other filters?
  switch (field.type) {
    case 'enum':
    case 'enum_set':
    case 'folder':
      break;

    default:
      return field;
  }

  const AsyncFolderOptionsRenderer: Filters.FieldRendererProps = ({
    Component,
    innerProps,
  }) => {
    const [getFieldValues, result] =
      api.endpoints.getFolderFilterOptions.useLazyQuery();
    const debouncedGetFieldValues = useCallback(debounce(getFieldValues, 500), [
      getFieldValues,
    ]);
    const { data, error, isFetching } = result;
    const options = (data || []).map((value) => ({
      value: value.value,
      label: value.display_value,
      description: value.path,
      leftIcon: 'folder',
    }));
    const asyncOptions = { data: options, error, isFetching };
    const fetchOptions = (substringQuery = '') => {
      debouncedGetFieldValues({ substringQuery }, true);
    };

    const asyncProps = {
      asyncOptions,
      onMount: fetchOptions,
      onQuery: fetchOptions,
    };

    return <Component asyncProps={asyncProps} innerProps={innerProps} />;
  };

  const AsyncOptionsContainer: Filters.FieldRendererProps = ({
    Component,
    innerProps,
  }) => {
    const [getFieldValues, result] =
      api.endpoints.getFieldValues.useLazyQuery();
    const debouncedGetFieldValues = useCallback(debounce(getFieldValues, 300), [
      getFieldValues,
    ]);
    const { data, error, isFetching } = result;
    const options = mapValueObjectsToOptions(data?.values || []);
    const asyncOptions = { data: options, error, isFetching };
    const fetchOptions = (substringQuery = '') => {
      debouncedGetFieldValues(
        {
          fieldId: field.id,
          searchSuggestions: true,
          substringQuery,
        },
        true,
      );
    };
    const asyncProps = {
      asyncOptions,
      onMount: fetchOptions,
      onQuery: fetchOptions,
    };

    return <Component asyncProps={asyncProps} innerProps={innerProps} />;
  };

  if (field.type === 'folder') {
    field.render = AsyncFolderOptionsRenderer;
  } else {
    field.render = AsyncOptionsContainer;
  }

  return field;
};

const FIELDS_WITH_COMPLEX_VALUES: string[] = ['document_group_id', 'folder'];

const attachSerializer = ({ field, values }: FilterSerializerProps): Field => {
  if (!FIELDS_WITH_COMPLEX_VALUES.includes(field.id)) {
    return field;
  }

  const filterSerializer = (filter: Filter) => {
    return values?.find((value) => value.value === filter.values[0])
      ?.display_value;
  };

  field.serialize = filterSerializer;

  return field;
};

const attachRenderersAndSerializers = ({
  fields,
  fieldValues,
}: FilterProps): Fields => {
  const entries = Object.entries(fields)
    // objects created by RTKQ are immutable, make a shallow copy, so we can extend it later
    .map(([fieldId, field]): [FieldId, Field] => [fieldId, { ...field }])
    .map(([fieldId, field]): [FieldId, Field] => [
      fieldId,
      updateSettings(field),
    ])
    .map(([fieldId, field]): [FieldId, Field] => [
      fieldId,
      attachRenderer(field),
    ])
    .map(([fieldId, field]) => [
      fieldId,
      attachSerializer({ field, values: fieldValues[field.id] }),
    ]);

  return Object.fromEntries(entries);
};

const PreFetchFieldValues = memo(({ fieldId }: { fieldId: string }) => {
  const dispatch = useDispatch();

  const result = api.endpoints.getFieldValues.useQuery({
    fieldId,
    searchSuggestions: true,
    substringQuery: '',
  });

  useEffect(() => {
    if (result.data) {
      dispatch(
        slices.automation.actions.setAutomationFieldValues({
          fieldId,
          values: result.data.values,
        }),
      );
    }
  }, [result]);

  return null;
});

const DataContainer = () => {
  const filters = useSelector(
    slices.automation.selectors.selectAutomationFilters,
  );
  const filtersRequiringFieldValues = filters.filter((filter: any) =>
    FIELDS_WITH_COMPLEX_VALUES.includes(filter.fieldId),
  );

  return (
    <>
      {filtersRequiringFieldValues.map((filter: any) => {
        if (filter.fieldId === 'folder') {
          return null;
        } else {
          return (
            <PreFetchFieldValues key={filter.id} fieldId={filter.fieldId} />
          );
        }
      })}
    </>
  );
};

interface SearchProps {
  fields: Fields;
  groups: Filters.Group[];
  preview: Nullable<PilotSearchQuery>;
}

const SearchFilters = memo(
  ({ preview, fields = {}, groups = [] }: SearchProps) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const setAutomationFilter = (filters: FiltersType) => {
      dispatch(slices.automation.actions.setAutomationFilter(filters));
    };

    const buildFolderOption = (folderTree: DropdownFieldValue[]) =>
      folderTree?.map((item) => ({
        label: item.display_value,
        value: item.value,
      })) ?? [];

    const filters = useSelector(
      slices.automation.selectors.selectAutomationFilters,
    );
    const fieldValues = useSelector(
      slices.automation.selectors.selectAutomationFieldValues,
    );
    const clauses = useSelector(
      slices.automation.selectors.selectAutomationClauseSuggestions,
    );
    const folderTree = useSelector(
      slices.automation.selectors.selectAutomationFolderTree,
    );

    const fieldsWithLazyLoad = useMemo(() => {
      const fieldItems = attachRenderersAndSerializers({
        fields,
        fieldValues: fieldValues as FieldValues,
      });

      fieldItems.folder = {
        ...fieldItems.folder,
        settings: {
          __testEnableSelectAll: (_: Filter<EnumValue>) => false,
          options: buildFolderOption(folderTree),
        },

        render: isEmpty(preview) ? fieldItems.folder.render : undefined,
      };

      fieldItems.clause = {
        ...fieldItems.clause,
        settings: {
          clauses,
        },
      };

      return fieldItems;
    }, [clauses, fields, fieldValues, folderTree]);

    if (preview != null) {
      if (preview.length <= 0)
        return (
          <Text>
            {intl.formatMessage({
              id: 'AutomationHubSearchingFilters.noFiltersPreview',
              defaultMessage: 'No Filter',
              description: 'Message shown when there are no filters to preview',
            })}
          </Text>
        );

      const supportedFieldEntities = preview.filter((filter) =>
        testIsSupportedFilterType(filter),
      );
      const previewFilters = toFilters(supportedFieldEntities) as FiltersType;

      return (
        <FiltersPreview fields={fieldsWithLazyLoad} filters={previewFilters} />
      );
    }

    return (
      <>
        <DataContainer />
        <Filters_DEPRECATED
          fields={fieldsWithLazyLoad}
          filters={filters}
          groups={groups}
          onChange={setAutomationFilter}
          options={{
            disableManageFilters: false,
          }}
        />
      </>
    );
  },
);

const AutomationHubSearchingFilters = ({
  preview,
  training = false,
  prediction = false,
  retraining = false,
}: {
  preview: Nullable<PilotSearchQuery>;
  training: boolean;
  prediction: boolean;
  retraining: boolean;
}) => {
  const dispatch = useDispatch();

  const { data: sectionsAndFolders, isFetching: isFetchingSections } =
    api.endpoints.getFilterSections.useQuery(
      without(getDefaultMoreFilterFieldIds(), 'clause'),
    );

  const { data: clauseSuggestions } =
    api.endpoints.getClauseSuggestions.useQuery(undefined);
  const { data: folderTreeData } =
    api.endpoints.getFolderTree.useQuery(undefined);

  const fields = sectionsAndFolders?.fields;
  const fieldGroups = sectionsAndFolders?.fieldGroups;

  const modelFilters = useSelector(
    slices.automation.selectors.selectAutomationModelFilter,
  );

  const predictions: PilotSearchQuery = modelFilters?.predictionFilter;
  const trainings: PilotSearchQuery = modelFilters?.trainingFilter;

  const folders = folderTreeData?.children;
  const folderTree = !folders ? [] : flattenFolderTree(folders);

  useEffect(() => {
    if (fields)
      dispatch(slices.automation.actions.setAutomationFilterFields(fields));
  }, [fields]);

  useEffect(() => {
    if (folderTree.length)
      dispatch(
        slices.automation.actions.setAutomationFilterFolderTree(folderTree),
      );
  }, [folderTree]);

  useEffect(() => {
    if (clauseSuggestions)
      dispatch(
        slices.automation.actions.setAutomationFilterClause(
          clauseSuggestions.suggestions,
        ),
      );
  }, [clauseSuggestions]);

  const ctf = values(fields).find((f) => f.label === 'Contract Type');

  if (prediction) {
    const supportedFieldEntities =
      predictions?.filter((filter) => testIsSupportedFilterType(filter)) ?? [];
    const filters = toFilters(supportedFieldEntities);
    dispatch(
      slices.automation.actions.setAutomationFilter(filters as FiltersType),
    );
  }

  if (retraining) {
    const supportedFieldEntities =
      trainings?.filter((filter) => testIsSupportedFilterType(filter)) ?? [];
    const filters = toFilters(supportedFieldEntities);

    dispatch(
      slices.automation.actions.setAutomationFilter(filters as FiltersType),
    );
  }

  if (training) {
    const filters = [
      { fieldId: ctf?.id, id: uuid(), operatorId: null, values: [] },
      { fieldId: 'folder', id: uuid(), operatorId: null, values: [] },
    ].filter((f) => !!f.fieldId);

    dispatch(
      slices.automation.actions.setAutomationFilter(filters as FiltersType),
    );
  }

  if (isFetchingSections || !fields || !fieldGroups) {
    return (
      <Layout w="100%" direction="column" align="center">
        <LoadingSpinner />
      </Layout>
    );
  }

  return (
    <SearchFilters preview={preview} fields={fields} groups={fieldGroups} />
  );
};

/* Prevent re-render when using filter on parent components */
export default React.memo(AutomationHubSearchingFilters);
